<template>
  <div>
    <!-- Loader -->
    <portal to="loaderTarget">
      <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
        <gd-loader :label="$t('frontend_loading')"/>
      </div>
    </portal>

    <div
      id="campaign-step-container"
      class="gs-app gm-campaign-create"
    >
      <div class="gm-campaign-create__banner-slot">
        <gd-banner
          class="fixed gm-campaign-create__banner"
          v-if="isLimitBannerVisible"
          :bannerMsg="limitBannerMessage"
          :bannerTextColor="bannerConfig.textColor"
          :bannerSize="bannerConfig.size"
          :bannerColor="bannerConfig.color"
          :bannerActions="bannerConfig.bannerActions"
        >
          <template slot="action-butons">
            <div class="gm-campaign-create__banner-actions">
              <gs-button
                :variant="bannerConfig.actionItems.actionBtnType === 'primary' ? 'primary' : 'outline'"
                size="sm"
                @click="goToUpgradePage()"
              >{{ $t(bannerConfig.actionItems.actionBtnText) }}</gs-button>
              <button
                type="button"
                class="gm-campaign-create__banner-dismiss"
                :class="[bannerConfig.textColor]"
                aria-label="Dismiss"
                @click="updateLimitBanner(false)"
              >
                <gs-icon name="x" size="16"></gs-icon>
              </button>
            </div>
          </template>
        </gd-banner>
      </div>

      <div
        class="gm-wizard"
        :class="{ 'gm-wizard--with-banner': isLimitBannerVisible }"
      >
        <!--
          Step navigation. Reproduces GdTabs exactly: in first-run mode steps after the
          current one are greyed (GdTabs' disableNotCompleted only styled them) and every
          click still goes to handleTabChange, which owns the gating and alerts; in edit
          mode (allStepsConfigured) every step goes to handleConfiguredWizardTabChange.
        -->
        <aside class="gm-wizard__sidebar" aria-label="Broadcast steps">
          <div class="gm-wizard__nav-heading">
            <span>Broadcast</span>
            <span
              class="gm-wizard__nav-progress"
              :class="{ 'is-complete': allStepsConfigured }"
              :aria-label="`${completedStepCount} of ${wizardSteps.length} steps complete`"
            >
              <gs-icon v-if="allStepsConfigured" name="check" size="14"></gs-icon>
              <span v-else>{{ completedStepCount }} / {{ wizardSteps.length }}</span>
            </span>
          </div>
          <button
            v-for="step in wizardSteps"
            :key="step.value"
            type="button"
            class="gm-wizard__nav-item"
            :class="{
              'is-active': step.active,
              'is-complete': step.completed,
              'is-upcoming': step.upcoming,
            }"
            :aria-current="step.active ? 'step' : null"
            :aria-disabled="step.upcoming ? 'true' : null"
            @click="allStepsConfigured ? handleConfiguredWizardTabChange(step.value) : handleTabChange(step.value)"
          >
            <span class="gm-wizard__nav-dot" aria-hidden="true"></span>
            <span class="gm-wizard__nav-label">{{ step.label }}</span>
          </button>
        </aside>

        <main class="gm-wizard__main">
          <div class="gm-wizard__intro">
            <span class="gm-wizard__eyebrow">Step {{ currentStepNumber }} of {{ wizardSteps.length }}</span>
            <h1 class="gm-wizard__title">{{ wizardIntro.heading }}</h1>
            <p v-if="wizardIntro.description" class="gm-wizard__subtitle">{{ wizardIntro.description }}</p>
          </div>

          <div class="gm-wizard__canvas">
            <div v-if="currentTab === 'step1'">
              <step1
                ref="step1"
                :model="model"
                :campignTags="campignTags"
                :integrations="integrations"
                @updateModel="model = $event"
                @fetchCampaignTags="fetchCampaignTags"
                @showLoader="showLoader"
                @hideLoader="hideLoader"
                @stepValidity="stepValidity"
                @on-validate="stepValidationSuccessful('step1', $event)"
              ></step1>
            </div>
            <div v-if="currentTab === 'step3'">
              <step3
                ref="step3"
                :model="model"
                @updateModel="model = $event"
                @stepValidity="stepValidity"
                @recipientsUpdated="updateLimitBanner(true)"
                @showLoader="showLoader"
                @hideLoader="hideLoader"
                @requestErrorAlert="requestErrorAlert"
                @on-validate="stepValidationSuccessful('step3', $event)"
              ></step3>
            </div>
            <div v-if="currentTab === 'step4'">
              <step4
                ref="step4"
                :model="model"
                :integrations="integrations"
                @updateModel="model = $event"
                @stepValidity="stepValidity"
                @saveOnEmailContentReset="submit()"
                @showLoader="showLoader"
                @hideLoader="hideLoader"
                @requestErrorAlert="requestErrorAlert"
                @on-validate="stepValidationSuccessful('step4', $event)"
              ></step4>
            </div>
            <div v-if="currentTab === 'step6'">
              <step6
                ref="step6"
                :model="model"
                @updateModel="model = $event"
                @switchToThisStep="switchToThisStep"
                @showLoader="showLoader"
                @hideLoader="hideLoader"
                @requestErrorAlert="requestErrorAlert"
                @on-validate="stepValidationSuccessful('step6', $event)"
              ></step6>
            </div>
          </div>

          <div
            v-if="!allStepsConfigured || currentTab == 'step6'"
            class="gm-wizard__footer"
          >
            <div class="gm-wizard__footer-start">
              <gs-button
                variant="outline"
                v-if="!(currentTab == 'step1') && !allStepsConfigured"
                @click="moveBackOnWizard()"
              >{{ $t('frontend_groovemail_analytics_list_campains_create_back') }}</gs-button>
            </div>
            <div class="gm-wizard__footer-end">
              <gs-button
                variant="primary"
                @click="moveNextOnWizard()"
                v-if="currentTab != 'step6' && !allStepsConfigured"
              >{{ $t('frontend_next') }}</gs-button>
              <gs-button
                variant="primary"
                v-if="currentTab == 'step6'"
                @click="saveAndExecuteCampaign()"
              >{{ finalActionLabel }}</gs-button>
            </div>
          </div>
        </main>
      </div>
    </div>

    <!-- Popup for free user after limit exceed Start -->
    <gs-named-modal
      ref="upgradeModal"
      name="upgrade-modal-for-limit"
      width="600px"
      max-width="100%"
      :title="$t('frontend_groovemail_upgrade_modal_heading')"
    >
      <upgrade-content
        @closeUpgradeModal="closeUpgradeModalForFreeUsers"
      ></upgrade-content>
    </gs-named-modal>

    <!-- Header Portal Next, Save, Done Editing buttons  -->
    <portal to="headerContent">
      <div class="gs-app gm-campaign-create-shell__actions">
        <gs-button
          variant="outline"
          class="hidden sm:block"
          v-if="!(currentTab == 'step1') && !allStepsConfigured"
          @click="moveBackOnWizard()"
        >{{ $t('frontend_groovemail_analytics_list_campains_create_back') }}</gs-button>
        <gs-button
          variant="outline"
          v-if="currentTab !== 'step6'"
          @click="saveAndExitWizard()"
        >{{
          $route.params.id
            ? $t('frontend_groovemail_campaigns_forms_done_editing')
            : $t('frontend_products_create_save_exit')
        }}</gs-button>
        <gs-button
          variant="primary"
          class="hidden sm:block"
          @click="moveNextOnWizard()"
          v-if="currentTab != 'step6' && !allStepsConfigured"
        >{{ $t('frontend_next') }}</gs-button>
        <gs-button
          variant="primary"
          v-if="currentTab == 'step6'"
          @click="saveAndExecuteCampaign()"
        >{{ finalActionLabel }}</gs-button>
      </div>
    </portal>

    <!-- Header Portal Go back button  -->
    <portal to="headerBreadCrumb">
      <button
        type="button"
        class="gs-app gm-campaign-create-shell__back"
        @click="moveBackToBroadcastListing"
      >
        <gs-icon name="arrow-left" size="16"></gs-icon>
        <span>{{ $route.params.id ? 'Edit broadcast' : 'New broadcast' }}</span>
      </button>
    </portal>

    <!-- Confirmation Modal move back -->
    <confirm-modal
      :ref="`confirmation-move-back`"
      :name="`confirmation-move-back`"
      :buttonText="$t('frontend_confirm')"
      :cancelText="$t('frontend_cancel')"
      :confirmMessage="this.$t('frontend_bouses_delete_bonus_title')"
      :messageContent="this.$t('frontend_groovemail_campaigns_forms_exit_without_saving')"
      @confirm="confirmMoveBack"
    />
  </div>
</template>

<script>
import auth from '@groovepages/gd-lib/auth';
import moment from 'moment';
import axios from 'axios';
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import Notify from '@groovepages/gd-ui-kit/notify';
import GDBanner from '@groovepages/gd-ui-kit/components/GDBanner';
import Campaign from '@/models/Campaign';
import themeConfig from '@/theme/themeConfig.js';
import showAlert from '@/components/sweet-alert-popup.js';
import UpgradeContent from '@/components/UpgradeContent.vue';
import { userEmailSendLimit } from '@/components/user-plan-limit/checkUserEmailSendLimit.js';

// importing steps
import step1 from './step1';
import step3 from './step3';
import step4 from './step4';
import step6 from './step6';

// import icons
import ExitIcon from '@/assets/images/icons/new/exit_arrow_new.svg';

export default {
  middleware: 'auth',
  layout: 'defaultWithoutSidebarLogo',

  metaInfo() {
    return {
      title: this.$t(
        'frontend_groovemail_components_pricepoint_sidebar_broadcasts'
      ),
    };
  },

  data() {
    return {
      allStepsConfigured: false,
      lastConfiguredStep: '',
      campaigns: [],
      last_page: '',
      postValidationTab: 'step3',
      currentTab: '',
      wizardColor: themeConfig.appPrimaryColor,
      assigned_to: null,
      mainWizardIndex: 0,
      fullPageLoader: true,
      isLoading: false,
      model: {
        editor_type: 3,
        campaignid: '',
        sendoption: 'email',
        name: '',
        description: '',
        campaign_tags: [],
        includedElements: [],
        condition: '',
        subscribeoption: [],
        totalsend: 0,
        totalleads: 0,
        filledemails: 0,
        blocked: 0,
        remainingEmails: 0,
        emailgateway: '',
        sender_id: null,
        postal_sequence_id: null,
        emailbroadcasttype: 'singleemail',
        emailsubject: '',
        emailpreviewtext: '',
        emailcontent: '',
        smscontent: '',
        emailContentSections: '',
        data: new FormData(),
        emailscheduled: 'savedraft',
        sheduledtime: new Date().toISOString(),
        gatewayname: '',
        email_id: null,
        template_name: '',
        template_folder: null,
      },
      tabAfterValid: 'step1',
      loaderColor: themeConfig.appPrimaryColor,
      sendemaillead: 'all',
      campignTags: [],
      integrations: [],
      showLimitBanner: false,
      bannerConfig: {
        size: 'regular',
        color: 'bg-primary-dark',
        textColor: 'text-primary-invert',
        bannerActions: true,
        actionItems: {
          actionBtnType: 'secondary',
          actionBtnText: 'frontend_groovemail_upgrade_btn',
        },
      },
    };
  },

  validations: {
    model: {
      name: { required, minLength: minLength(3), maxLength: maxLength(50) },
    },
  },

  components: {
    step1,
    step3,
    step4,
    step6,
    UpgradeContent,
    'gd-banner': GDBanner,
    ExitIcon,
  },

  methods: {
    updateLimitBanner(show) {
      this.showLimitBanner = show;
    },

    // fetch the Template data based on the compaign email_id
    async fetchTemplateData(id) {
      this.showLoader();
      try {
        const templateData = await axios.get(`email/template/${id}`);
        this.hideLoader();
        await this.assignTemplateDataInBuilder(templateData);

        // hide the loader
        this.hideLoader();
      } catch (error) {
        // show alert
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    assignTemplateDataInBuilder(templateData) {
      if (templateData && templateData.data) {
        this.model.template_name = templateData.data.name;
        this.model.template_folder = templateData.data.category_id;
        this.model.emailcontent = templateData.data.message
          ? templateData.data.message
          : '';
        const emailSections = templateData.data.emailContentSections
          ? templateData.data.emailContentSections
          : '';
        this.model.emailContentSections = emailSections || '';
      }
    },

    stepValidity(valid, step) {
      if (this.allStepsConfigured) {
        if (valid) {
          this.lastConfiguredStep = '';
        } else {
          this.lastConfiguredStep = step;
        }
      }
    },

    async handleConfiguredWizardTabChange(newTab) {
      if (this.lastConfiguredStep) {
        const refToValidate = this.$refs[this.lastConfiguredStep];
        if (await refToValidate.validateForSaveAndExit()) {
          this.currentTab = newTab;
        }
      } else {
        this.currentTab = newTab;
      }
    },

    getLastConfiguredStep() {
      // sendoption validation
      const sendoption = this.model.sendoption &&
        this.model.name &&
        this.model.campaign_tags.length > 0 &&
        this.model.subscribeoption &&
        this.model.subscribeoption.length &&
        this.model.filledemails;

      // email validation
      const emailValid = this.model.sendoption === 'email' &&
          this.model.sender_id &&
          this.model.sender_id !== -1 &&
          this.model.emailcontent &&
          this.model.emailsubject;
        
      // sms validation
      const smsValid = this.model.sendoption === 'sms' &&
          this.model.sender_id &&
          this.model.sender_id !== -1 &&
          this.model.smscontent;

      // postal validation
      const postalValid = this.model.sendoption === 'postal' && this.model.postal_sequence_id;

      if (sendoption) {
        if(emailValid || smsValid || postalValid) {
          this.currentTab = 'step6';
          this.allStepsConfigured = true;
        } else {
          this.currentTab = 'step4';
        }
      } else if (this.model.sendoption && this.model.name && this.model.campaign_tags.length > 0) {
        this.currentTab = 'step3';
      } else if (!(this.model.sendoption && this.model.name && this.model.campaign_tags.length > 0)) {
        this.currentTab = 'step1';
      } else {
        this.currentTab = 'step1';
      }
    },

    moveNextOnWizard() {
      const currentlySelectedTabIndex = this.tabs
        .map((item) => item.value)
        .indexOf(this.currentTab);
      const nextTab = this.tabs[currentlySelectedTabIndex + 1].value;
      this.postValidationTab = nextTab;

      // get the index of the nextTab
      const indexOfNextTab = this.tabs.findIndex(item => item.value === nextTab);
      // get the index of the tabAfterValid
      const indexOfTabAfterValid = this.tabs.findIndex(item => item.value === this.tabAfterValid);

      if(indexOfTabAfterValid < indexOfNextTab) {
        // set the next valid tab value
        this.tabAfterValid = nextTab;
      }
      const refToValidate = this.$refs[this.currentTab];
      return refToValidate.validate();
    },

    async moveBackOnWizard() {
      // Get the index of the currently selected tab
      const currentlySelectedTabIndex = this.tabs
        .map((item) => item.value)
        .indexOf(this.currentTab);

      // If the currently selected tab is the fourth one (index 3)
      if (currentlySelectedTabIndex === 3) {
        // Set the email status to 'savedraft' and the scheduled time to the current time
        this.model.emailscheduled = 'savedraft';
        this.model.sheduledtime = new Date().toISOString();
      }

      // Get the value of the tab before the currently selected one
      const nextTab = this.tabs[currentlySelectedTabIndex - 1].value;
      // Set the post-validation tab to the tab before the currently selected one
      this.postValidationTab = nextTab;
      
      // Check if the current step is valid
      const isStepValid = await this.checkIsStepValid();
      // If the current step is not valid
      if (isStepValid === false) {
        // Set the tab after the valid one to the currently selected tab
        this.tabAfterValid = this.tabs[currentlySelectedTabIndex].value;
      }
      
      // Set the current tab to the tab before the currently selected one
      this.currentTab = nextTab;
      // Scroll to the top of the page
      this.scrollToTop();
    },

    // checks the step validation and return the boolean value
    async checkIsStepValid() {
      // Get the reference of the currently selected tab
      const refToValidate = this.$refs[this.currentTab];
      
      let isStepValid = false;

      // Check the validation of the current step
      if(this.currentTab === 'step1') {
        isStepValid = await refToValidate.validate();
      } else if (this.currentTab === 'step3') {
        isStepValid = await refToValidate.validateBack();
      } else if (this.currentTab === 'step4') {
        isStepValid = await refToValidate.validateForSaveAndExit();
      }

      // Return the validation result
      return isStepValid;
    },

    // open delete category modal
    async openMoveBackConfirmModal() {
      // open the modal
      this.$refs[`confirmation-move-back`].open();
    },

    // confirm delete category
    async confirmMoveBack() {
      this.model.emailscheduled = 'savedraft';
      this.model.sheduledtime = new Date().toISOString();
      this.$router.push({ name: 'Broadcasts' });
    },

    // move back to the broadcast listing
    async moveBackToBroadcastListing() {
      const changeMadeInCurrentStep = this.$refs[this.currentTab].checkChangesMadeOnThisStep();
      if (changeMadeInCurrentStep) {
        // open the modal to confirm the move back
        this.openMoveBackConfirmModal();
      } else {
        this.model.emailscheduled = 'savedraft';
        this.model.sheduledtime = new Date().toISOString();
        this.$router.push({ name: 'Broadcasts' });
      }
    },

    switchToThisStep(step) {
      this.currentTab = step;
      this.scrollToTop();
    },

    async saveAndExitWizard() {
      const currentlySelectedTabIndex = this.tabs
        .map((item) => item.value)
        .indexOf(this.currentTab);
      const nextTab = this.tabs[currentlySelectedTabIndex + 1].value;
      this.postValidationTab = nextTab;
      const refToValidate = this.$refs[this.currentTab];
      const isStepValid = await refToValidate.validateForSaveAndExit();
      this.model.emailscheduled = 'savedraft';
      this.model.sheduledtime = new Date().toISOString();

      // check the step validation
      if (isStepValid) {
        // show loader
        this.showLoader();

        try {
          if (this.model.campaignid !== '') {
            await this.submit();
          } else {
            await this.createCampaign();
          }  
          // hide the loader
          this.hideLoader();
        } catch (error) {
          // show alert
          this.requestErrorAlert(error.response?.data?.error);
        }

        // move to the next step
        this.$router.push({ name: 'Broadcasts' });
      }
    },

    async saveAndExecuteCampaign() {
      // send option validation
      const sendOption = this.model.sendoption &&
        this.model.name &&
        this.model.subscribeoption &&
        this.model.subscribeoption.length;

      // sendoption validation
      const emailValid = this.model.sendoption === 'email' &&
          this.model.sender_id &&
          this.model.sender_id !== -1 &&
          this.model.emailcontent &&
          this.model.emailsubject;
      
      // sms validation
      const smsValid = this.model.sendoption === 'sms' &&
          this.model.sender_id &&
          this.model.sender_id !== -1 &&
          this.model.smscontent;

      // postal validation
      const postalValid = this.model.sendoption === 'postal' && this.model.postal_sequence_id;
      
      if (
        this.model.remainingEmails <= 0 &&
        this.model.remainingEmails !== -1 &&
        this.model.emailscheduled !== 'savedraft'
      ) {
        this.openUpgradeModalForFreeUsers();
      } else if (sendOption && (emailValid || smsValid || postalValid)) {
        this.showLoader();
        this.prepareFields();
        
        // save the campaign data
        try {
          await this.campaign.updateCampaign(this.model.campaignid, this.model);
          await this.finalizeBroadcast();
          
          // hide the loader
          this.hideLoader();
        } catch (error) {
          // show alert
          this.requestErrorAlert(error.response?.data?.error);
        }
      } else {
        // hide the loader
        this.hideLoader();

        Notify.fail(
          this.$t(
            'frontend_groovemail_campaigns_wizard_broadcasts_campaign_type_changed_warning'
          )
        );
      }
    },

    async finalizeBroadcast() {
      try {
        await axios.get(`/sendemailleads/${this.model.campaignid}`);
        this.hideLoader();
        this.resetData();
        this.$router.push({ name: 'Broadcasts' });
      } catch (error) {
        if (error.response?.data?.error) {
          this.showErrorMessage(
            `${this.$t(
              'frontend_groovemail_sender_testing_broadcast_sender'
            )}: ${error.response?.data?.message}`
          );
        } else {
          this.showErrorMessage(
            `${this.$t(
              'frontend_groovemail_sender_testing_broadcast_sender'
            )}: ${this.$t('frontend_error')}`
          );
        }
        this.hideLoader();
      }
    },

    showErrorMessage(errorMessage) {
      showAlert({
        iconType: 'alert-error',
        html: `
          <style>.swal2-confirm { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-lg font-bold text-deep-dark mb-3"> ${this.$t('frontend_groovemail_sender_testing_sender_verification_error')}</p>
          <p class="text-sm text-deep-dark"> ${errorMessage}</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t('frontend_close'),
      });
    },

    showErrorMessageForFreeUser(errorMessage) {
      showAlert({
        iconType: 'alert-error',
        html: `
          <style>.swal2-confirm { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-sm text-deep-dark"> ${errorMessage}</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t('frontend_close'),
      });
    },

    async createCampaign() {
      this.$v.model.name.$touch();

      if (!this.$v.model.name.$invalid) {
        this.showLoader();
        this.campaign = new Campaign({});
        this.campaign.name = this.model.name;
        this.campaign.campaign_tags = this.model.campaign_tags;
        this.campaign.method = this.model.sendoption;
        this.campaign.emailscheduled = 'savedraft';

        // created and save the campaign data
        await this.campaign.storeCampaign(this.campaign)
          .then((response) => {
            this.model.campaignid = response.data.id;
            
            // add id to the route
            this.$router.push({ path: `${this.$route.path}/${response.data.id}` });
            
            // move to the next step
            this.currentTab = this.postValidationTab;
            
            // reset the data
            this.scrollToTop();

            // hide the loader
            this.hideLoader();
          })
          .catch((error) => {
            // show alert
            this.requestErrorAlert(error.response?.data?.error);
          });

      }
    },

    async stepValidationSuccessful(name, isValid) {
      if (isValid) {
        try {
          if (this.model.campaignid !== '') {
            await this.submit();
          } else {
            await this.createCampaign();
          }
        } catch (error) {
          // show error alert
          this.requestErrorAlert(error.response?.data?.error);

          return;
        }

        // move to the next step
        this.currentTab = this.postValidationTab;
        this.scrollToTop();
      }
    },

    // This function handles the change of tabs before save and exit
    async handleTabChange(newTab) {
      // Get the index of the new tab, the current tab, and the tab after the valid tab
      const indexOfTabAfterValid = this.tabs.findIndex(item => item.value === this.tabAfterValid);
      const indexOfNewTab = this.tabs.findIndex(item => item.value === newTab);
      const indexOfCurrentTab = this.tabs.findIndex(item => item.value === this.currentTab);

      // Check the validation of the current step and store the result
      const isStepValid = await this.checkIsStepValid();
      
      // If trying to move forward and the current step is not valid, show an error message and prevent the tab change
      if (indexOfNewTab > indexOfCurrentTab && !isStepValid) {
        // Show the alert message
        const alertMessage = this.$t('frontend_groovemail_campaigns_wizard_next_step_warning_message');  
        this.showErrorMessageForFreeUser(alertMessage);
        
        // Stop the tab change
        return;
      }

      // If trying to skip steps and the skipped steps are not valid, show an error message
      if(
        indexOfNewTab > indexOfCurrentTab &&
        indexOfNewTab > indexOfTabAfterValid &&
        indexOfNewTab !== indexOfCurrentTab + 1
      ) {
        // Show the alert message
        const alertMessage = this.$t('frontend_groovemail_campaigns_wizard_next_step_warning_message_complete_previous_steps');  
        this.showErrorMessageForFreeUser(alertMessage);

        // Stop the tab change
        return;
      }

      // If the current step is not valid, set the tabAfterValid to the current tab
      if(!isStepValid && indexOfNewTab < indexOfCurrentTab) {
        this.tabAfterValid = this.tabs[indexOfCurrentTab].value;
      }
      
      // If trying to move to a previous step or the next step and the current step is valid, allow the tab change
      if (
        indexOfNewTab <= indexOfTabAfterValid ||
        (isStepValid && indexOfNewTab === indexOfCurrentTab + 1)
      ) {
        // Set the current tab to the new tab
        this.currentTab = newTab;
        // Scroll to the top of the page
        this.scrollToTop();
      }
    },

    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },

    showcampaign(Id) {
      this.$router.push({ name: 'CampaignDetail', params: { id: Id } });
    },

    prepareFields() {
      // Append the form data to the model data object
      this.model.data.append('name', this.model.name);
      this.model.data.append('description', this.model.description ? this.model.description : '');
      this.model.data.append('campaign_tags', this.model.campaign_tags);
      this.model.data.append('sender_id', this.model.sender_id ? this.model.sender_id : '');
      this.model.data.append('method', this.model.sendoption);

      // Append the postal sequence ID if the send option is postal
      if (this.model.sendoption === 'postal' && this.model.postal_sequence_id) {
        this.model.data.append('postal_sequence_id', this.model.postal_sequence_id);
      }

      this.model.data.append('gateway', this.model.emailgateway);
      this.model.data.append('conditions', this.model.subscribeoption);
      this.model.data.append('emailtype', this.model.emailbroadcasttype);
      this.model.data.append('subject', this.model.emailsubject ? this.model.emailsubject : '');
      this.model.data.append('previewtext', this.model.emailpreviewtext ? this.model.emailpreviewtext : '');

      // Append the email content and editor type if the send option is email
      if (this.model.sendoption === 'email') {
        this.model.data.append('editor_type', this.model.editor_type ? this.model.editor_type : 1);
        this.model.data.append('message', this.model.emailcontent ? this.model.emailcontent : '');
        this.model.data.append(
          'emailContentSections',
          this.model.emailContentSections
            ? typeof this.model.emailContentSections === 'object'
              ? JSON.stringify(this.model.emailContentSections)
              : this.model.emailContentSections
            : ''
        );
      }

      // Append the SMS content if the send option is SMS
      else if (this.model.sendoption === 'sms') {
        this.model.data.append('message', this.model.smscontent ? this.model.smscontent : '');
      }

      // Append the email ID if it exists
      this.model.data.append('email_id', this.model.email_id ? this.model.email_id : '');

      // Append the template name and folder if they exist
      if (this.model.template_name) {
        this.model.data.append('template_name', this.model.template_name);
      }
      if (this.model.template_folder) {
        this.model.data.append('category_id', this.model.template_folder);
      }

      // Append the email scheduled and scheduled time
      this.model.data.append('emailscheduled', this.model.emailscheduled);
      this.model.data.append('sheduledtime', this.model.sheduledtime);
    },

    // Submit the new campaign data
    async submit() {
      // show loader
      this.showLoader();
      
      try {
        const response = await this.campaign.updateCampaign(this.model.campaignid, this.model);
       
        // Prepare the fields
        this.prepareFields();

        // check the email ID in the response data and set the email ID in the model
        if (response.data.email_id || response.data.template_id) {
          this.model.email_id = response.data.email_id || response.data.template_id;
        }

        // reset the data
        this.resetData();
        
        // hide the loader
        this.hideLoader();
        
        // scroll to top
        this.scrollToTop();
        
      } catch(error) {
        // make the last tab as the current tab
        this.tabAfterValid = this.currentTab;
        
        // show alert
        this.requestErrorAlert(error.response?.data?.error);
        
        return;
      };
    },

    // reset the data
    resetData() {
      this.model.data = new FormData(); // Reset it completely
    },

    requestErrorAlert(error) {
      // hide loader
      this.hideLoader();

      showAlert({
        iconType: 'alert-error',
        html : `
          <style>.swal2-cancel { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-sm text-deep-dark"> ${ error || this.$t('frontend_groovemail_forms_view_unknown_backend_error') }</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t('frontend_close'),
      });
    },

    // fetch the campaign tags data
    async fetchCampaignTags() {
      try {
        const campaignTagData = await axios.get('/get-campaign-tags');
        this.campignTags = campaignTagData?.data?.data
          ? campaignTagData.data.data
          : [];
      } catch (error) {
        // show the error message
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    fetchClosemIntegeration() {
      const user = auth.authenticatedUser();
      if (user.metadata && user.metadata.integrations) {
        this.integrations = user.metadata.integrations.filter(
          (ele) => ele.type === 'closem-integration'
        );
      }
    },

    openUpgradeModalForFreeUsers() {
      this.$root.$emit('modal-open', 'upgrade-modal-for-limit');
    },

    closeUpgradeModalForFreeUsers() {
      this.$root.$emit('modal-close', 'upgrade-modal-for-limit');
    },

    goToUpgradePage() {
      const user = auth.authenticatedUser();
      const url = window.gdEnv[`UPGRADE_URL_${user?.metadata?.region?.toUpperCase()}`] || window.gdEnv[`UPGRADE_URL`];
      window.open(url, '_blank');
    },

    // scroll to top of the page
    scrollToTop() {
      document.getElementById('campaign-step-container').scrollTop = 0;
    },

    
    // checks the route and set the campaign id
    checkTheCampainIdOnRoute() {
      if (this.$route.params.id) {
        this.model.campaignid = this.$route.params.id;
      }
    },

    // fetch the campaign data based on the campaign id
    async checkEmailIdOnCampaign() {
      if (this.campaign.email_id || this.campaign.template_id) {
          this.model.editor_type = 3;
          this.model.email_id = this.campaign.email_id || this.campaign.template_id;
          await this.fetchTemplateData(this.model.email_id);
        } else {
          if (this.campaign.message) {
            if(this.campaign.message === 'null') {
              this.model.emailcontent = '';
            } else {
              this.model.emailcontent = this.campaign.message;
            }
          } else {
            this.model.emailcontent = '';
          }
          const emailSections = this.campaign.emailContentSections
            ? JSON.parse(this.campaign.emailContentSections)
            : '';
          this.model.emailContentSections = emailSections || '';
        }
    },

    // check send option type and set the editor type
    async checkSendOptionType() {
      if (this.model.sendoption === 'email') { // If the send option is email, set the editor type based on the campaign data
        // check the email ID in the campaign data and set the editor type to default if the email ID is not set
        if(this.campaigns.editor_type === 3 && !this.campaign.email_id && !this.campaign.template_id) {
          this.model.editor_type = 1;

          return;
        }

        if (this.campaign.editor_type) {
          this.model.editor_type = +this.campaign.editor_type;
        } else {
          this.model.editor_type = 3;
        }
        
        // check the email ID in the campaign data
        await this.checkEmailIdOnCampaign();
        
      } else if (this.model.sendoption === 'sms') { // If the send option is SMS, set the SMS content based on the campaign data
        if (this.campaign.message) {
          if (this.campaign.message === 'null') {
            this.model.smscontent = '';
          } else {
            this.model.smscontent = this.campaign.message;
          }
        } else {
          this.model.smscontent = '';
        }
      } else if (this.model.sendoption === 'postal') { // If the send option is postal, set the postal sequence ID based on the campaign data
        this.model.postal_sequence_id = this.campaign.postal_sequence_id
          ? this.campaign.postal_sequence_id
          : null;
      }
    },

    // check the campaign data and set the model properties
    async setPropertiesFromData() {
      // Set the model properties based on the campaign data
      this.model.emailbroadcasttype = this.campaign.emailtype;

      // Set the email subject based on the campaign data
      if (this.campaign.subject) {
        if (this.campaign.subject === 'null') {
          this.model.emailsubject = '';
        } else {
          this.model.emailsubject = this.campaign.subject;
        }
      } else {
        this.model.emailsubject = '';
      }
      
      // Set the email preview text based on the campaign data
      if (this.campaign.previewtext) {
        if (this.campaign.previewtext === 'null') {
          this.model.emailpreviewtext = '';
        } else {
          this.model.emailpreviewtext = this.campaign.previewtext;
        }
      } else {
        this.model.emailpreviewtext = '';
      }
      
      // check the set send option type
      await this.checkSendOptionType();

      // Set the email scheduled and scheduled time based on the campaign data
      this.model.emailscheduled = this.campaign.emailscheduled;
      this.model.sheduledtime = this.campaign.actualdate
        ? moment.utc(this.campaign.actualdate).toISOString()
        : new Date().toISOString();
    },

    // check the active leads and fetch the lead data
    async checkTheActiveLeads() {
    
      // If there are active leads, fetch the lead data
      if (this.model.subscribeoption && this.model.subscribeoption.length) {
        const { data } = await axios.get('/campaignsleads', {
          params: {
            include_conditions: this.model.subscribeoption,
            include_type: 'OR',
            method_type: this.model.sendoption,
          },
        });

        // Set the model properties based on the lead data
        this.model.totalleads = data.totalleads;
        this.model.filledemails = data.filledemails;
        this.model.blocked = data.blocked;
        this.model.totalsend = data.totalsend;
        this.model.remainingEmails = data.remaining_emails
          ? data.remaining_emails
          : 0;

        // Update the limit banner to show the remaining emails
        this.updateLimitBanner(true);
      } else {
        // If there are no active leads, set the remaining emails based on the send option
        this.model.remainingEmails = await userEmailSendLimit(this.model.sendoption);

        // Update the limit banner to show the remaining emails
        this.updateLimitBanner(true);
      }
    }
  },

  async created() {
    try {
      // Show the loader while data is being fetched
      this.showLoader();

      // Fetch campaign tags and Closem integration data
      await this.fetchCampaignTags();
      await this.fetchClosemIntegeration();
  
      // If there is no campaign ID in the route params, create a new campaign
      if (this.$route.params.id === undefined) {
        // Set the remaining emails based on the send option
        this.model.remainingEmails = await userEmailSendLimit(this.model.sendoption);
  
        // Update the limit banner to show the remaining emails
        this.updateLimitBanner(true);
  
        // Create a new Campaign object and set the current tab to 'step1'
        this.campaign = new Campaign({});
        this.currentTab = 'step1';
  
        // Hide the loader
        this.hideLoader();
      } else {
        // If there is a campaign ID in the route params, fetch the campaign data
        this.campaign = await Campaign.$find(this.$route.params.id);
  
        // Set the model properties based on the campaign data
        this.model.name = this.campaign.name;
        this.model.description = this.campaign.description;
        if (this.campaign.campaign_tags && this.campaign.campaign_tags.length) {
          this.model.campaign_tags = this.campaign.campaign_tags;
        }
        this.model.campaignid = this.$route.params.id;
        this.model.sender_id = this.campaign.sender_id;
        this.model.emailgateway = this.campaign.gateway;
        this.model.gatewayname =
          this.campaign.gateway !== '' || this.campaign.gateway !== 'null'
            ? this.campaign.gateway
            : `${this.branddata.apps.groovemail?.name || 'GrooveMail'} server`;
        this.model.sendoption = this.campaign.method;

        // check the campaign conditions
        if(typeof this.campaign.conditions === 'string') {
          this.model.subscribeoption = this.campaign.conditions.length > 0 ? JSON.parse(this.campaign.conditions) : [];
        } else {
          this.model.subscribeoption = this.campaign.conditions;
        }
  
        // If there are active leads, fetch the lead data
        await this.checkTheActiveLeads();
        
        // Set the model properties based on the campaign data
        await this.setPropertiesFromData();
  
        // Get the last configured step and set the current tab accordingly
        await this.getLastConfiguredStep();
  
        // set the tabAfterValid value from the currentTab
        this.tabAfterValid = this.currentTab;
      }
  
      // Hide the loader
      this.hideLoader();

    } catch (error) {
      // show the error alert
      this.requestErrorAlert(error.response?.data?.error);
    }

  },

  async mounted() {
    this.model.gatewayname = `${this.supBrandName} server`;
  },

  computed: {
    // Get the brand data from the store
    supBrandName() {
      return this.$store.getters.supBrand.title;
    },

    tabs() {
      return [
        {
          title: `<span class="text-sm  font-semibold"><i class="fa fa-check" aria-hidden="true"></i>&nbsp;
              ${this.$t('frontend_groovemail_campaign_tabs_Info')}
              <i class="mr-2 ml-1 text-xs fas fa-chevron-right" aria-hidden="true"></i><span>
            `,
          value: 'step1',
        },
        {
          title: `<span class="text-sm  font-semibold"><i class="fa fa-check" aria-hidden="true"></i>&nbsp;
            ${this.$t('frontend_groovemail_campains_show_recipients')}
            <i class="mr-2 ml-1 text-xs fas fa-chevron-right" aria-hidden="true"></i><span>
          `,
          value: 'step3',
        },
        {
          title: `<span class="text-sm  font-semibold"><i class="fa fa-check" aria-hidden="true"></i>&nbsp;
            ${this.$t('frontend_content')}
            <i class="mr-2 ml-1 text-xs fas fa-chevron-right" aria-hidden="true"></i><span>
          `,
          value: 'step4',
        },
        {
          title: `<span class="text-sm  font-semibold last-child"><i class="fa fa-check" aria-hidden="true"></i>&nbsp;
            ${this.$t('frontend_groovemail_campaign_tabs_send')}
            <i class="mr-2 ml-1 text-xs fas fa-chevron-right" aria-hidden="true"></i><span>
          `,
          value: 'step6',
        },
      ];
    },

    mainTabClass() {
      return 'pb-4 gs-default-tabs-item';
    },

    limitBannerMessage() {
      return `${this.$t(
        'frontend_groovemail_broadcasts_remaining_count_message_1'
      )} ${this.model.remainingEmails} ${this.$t(
        'frontend_groovemail_broadcasts_remaining_count_message_2'
      )}`;
    },

    // --- Template-only additions for the design-system wizard shell (no behaviour change) ---

    // Sidebar model. Mirrors GdTabs: "completed" = steps before the current one (or every
    // other step once allStepsConfigured); "upcoming" = steps after it in first-run mode,
    // which GdTabs' disableNotCompleted only greyed out.
    wizardSteps() {
      const labels = {
        step1: this.$t('frontend_groovemail_campaign_tabs_Info'),
        step3: this.$t('frontend_groovemail_campains_show_recipients'),
        step4: this.$t('frontend_content'),
        step6: this.$t('frontend_groovemail_campaign_tabs_send'),
      };
      const currentIndex = this.tabs.findIndex((tab) => tab.value === this.currentTab);
      return this.tabs.map((tab, index) => ({
        value: tab.value,
        label: labels[tab.value] || tab.value,
        active: tab.value === this.currentTab,
        completed: this.allStepsConfigured ? tab.value !== this.currentTab : index < currentIndex,
        upcoming: !this.allStepsConfigured && index > currentIndex,
      }));
    },

    currentStepNumber() {
      const index = this.tabs.findIndex((tab) => tab.value === this.currentTab);
      return index < 0 ? 1 : index + 1;
    },

    completedStepCount() {
      return this.wizardSteps.filter((step) => step.completed).length;
    },

    isLimitBannerVisible() {
      return (
        this.showLimitBanner &&
        this.model.remainingEmails !== -1 &&
        this.model.remainingEmails < this.model.filledemails
      );
    },

    // Eyebrow / title / subtitle for the content column (step titles used to live in each step file).
    wizardIntro() {
      const intros = {
        step1: {
          heading: this.$t('frontend_groovemail_campaigns_wizard_broadcasts_campaign_info_title'),
          description: 'Choose how this broadcast goes out, then give it a name and a category.',
        },
        step3: {
          heading: this.$t('frontend_groovemail_campains_show_recipients'),
          description: this.$t('frontend_groovemail_campaigns_wizard_broadcasts_campaign_recipients_who_get'),
        },
        step4: {
          heading: this.model.sendoption === 'postal'
            ? this.$t('frontend_groovemail_campaigns_create_postal_integration_postal_content_page_header')
            : this.$t('frontend_groovemail_campaigns_wizard_broadcasts_campaign_campaign_content_label'),
          description: this.model.sendoption === 'postal'
            ? ''
            : 'Pick a sender and write the message. Send yourself a test before moving on.',
        },
        step6: {
          heading: this.$t('frontend_groovemail_campaigns_wizard_broadcasts_campaign_ready_to_send'),
          description: this.$t('frontend_groovemail_campaigns_wizard_broadcasts_campaign_review_campaign'),
        },
      };
      return intros[this.currentTab] || { heading: '', description: '' };
    },

    // Label of the final primary action (header and footer share it).
    finalActionLabel() {
      if (this.model.emailscheduled == 'sendnow') {
        return this.$t('frontend_groovemail_campains_step6_send_now');
      }
      if (this.model.emailscheduled == 'sendlater') {
        return this.$t('frontend_groovemail_campains_step6_schedule_for');
      }
      return this.$t('frontend_groovemail_campains_step6_save_as_draft');
    },
  },
};
</script>

<style>
/*
 * Broadcast wizard shell (DESIGN.md §4.1, September 2026 Design System).
 * Tokens come from the `.gs-app` root. Every rule is scoped under
 * `.gm-campaign-create`; the two header portals render inside HeaderBar
 * (outside this DOM subtree) so they carry the unique
 * `gm-campaign-create-shell__` prefix instead.
 */
.gm-campaign-create {
  --gm-wizard-content-width: 860px;
  height: calc(100vh - 65px);
  overflow-y: auto;
  scroll-behavior: smooth;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gm-campaign-create__banner-slot {
  position: relative;
  width: 100%;
}

.gm-campaign-create__banner {
  z-index: 30;
}

.gm-campaign-create__banner-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.gm-campaign-create__banner-dismiss {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  color: inherit;
  background: transparent;
  border: 0;
  border-radius: var(--gs-radius-sm);
  cursor: pointer;
}

/* Shell header portals */
.gm-campaign-create-shell__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 64px;
}

.gm-campaign-create-shell__back {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 64px;
  padding: 0;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  line-height: 1;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.gm-campaign-create-shell__back .gs-icon {
  color: var(--gs-muted-foreground);
}

.gm-campaign-create-shell__back:focus-visible,
.gm-campaign-create .gm-wizard__nav-item:focus-visible,
.gm-campaign-create .gm-choice-card:focus-visible,
.gm-campaign-create .gm-link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}

/* Two-column wizard layout: sticky step sidebar + centred content column */
.gm-campaign-create .gm-wizard {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 44px;
  max-width: 1260px;
  margin: 0 auto;
  padding: 32px 28px 56px;
}

.gm-campaign-create .gm-wizard--with-banner {
  padding-top: 72px;
}

.gm-campaign-create .gm-wizard__sidebar {
  position: sticky;
  top: 24px;
  align-self: start;
}

.gm-campaign-create .gm-wizard__nav-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 8px 12px;
  color: var(--gs-muted-foreground);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  line-height: 1;
  text-transform: uppercase;
}

.gm-campaign-create .gm-wizard__nav-progress {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 34px;
  margin-right: 12px;
  color: var(--gs-muted-foreground);
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0;
  line-height: 1;
}

.gm-campaign-create .gm-wizard__nav-progress.is-complete {
  color: var(--gs-success);
}

.gm-campaign-create .gm-wizard__nav-item {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 38px;
  padding: 6px 12px;
  color: var(--gs-muted-foreground);
  font: inherit;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.25;
  text-align: left;
  background: transparent;
  border: 0;
  border-left: 3px solid transparent;
  border-radius: var(--gs-radius-sm);
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}

.gm-campaign-create .gm-wizard__nav-item:hover {
  color: var(--gs-foreground);
  background: var(--gs-muted);
}

.gm-campaign-create .gm-wizard__nav-item.is-active {
  color: var(--gs-foreground);
  font-weight: 700;
  background: var(--gs-primary-soft);
  border-left-color: var(--gs-primary);
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

/* First-run mode: steps ahead of the current one read as not-yet-available (GdTabs' disableNotCompleted look). */
.gm-campaign-create .gm-wizard__nav-item.is-upcoming,
.gm-campaign-create .gm-wizard__nav-item.is-upcoming:hover {
  color: var(--gs-border-strong);
}

.gm-campaign-create .gm-wizard__nav-dot {
  display: inline-block;
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  margin-right: 12px;
  background: transparent;
  border: 1.5px solid var(--gs-border-strong);
  border-radius: 999px;
}

.gm-campaign-create .gm-wizard__nav-item.is-active .gm-wizard__nav-dot {
  border-color: var(--gs-primary);
}

.gm-campaign-create .gm-wizard__nav-item.is-complete .gm-wizard__nav-dot {
  background: var(--gs-success);
  border-color: var(--gs-success);
}

.gm-campaign-create .gm-wizard__nav-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Content column */
.gm-campaign-create .gm-wizard__main {
  min-width: 0;
}

.gm-campaign-create .gm-wizard__intro {
  margin-bottom: 20px;
  text-align: center;
}

.gm-campaign-create .gm-wizard__eyebrow {
  display: block;
  margin-bottom: 8px;
  color: var(--gs-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.gm-campaign-create .gm-wizard__title {
  margin: 0;
  color: var(--gs-foreground);
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
}

.gm-campaign-create .gm-wizard__subtitle {
  max-width: 560px;
  margin: 8px auto 0;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.55;
}

.gm-campaign-create .gm-wizard__canvas {
  width: 100%;
  max-width: var(--gm-wizard-content-width);
  margin: 0 auto;
}

.gm-campaign-create .gm-wizard__canvas > div {
  width: 100%;
}

.gm-campaign-create .gm-wizard__footer {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: var(--gm-wizard-content-width);
  margin: 20px auto 0;
  padding-top: 18px;
  border-top: 1px solid var(--gs-border);
}

.gm-campaign-create .gm-wizard__footer-end {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* The single source of truth for every step's summary card. */
.gm-campaign-create .gs-wizard-card {
  padding: 20px 24px;
  color: var(--gs-foreground);
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gm-campaign-create .gs-wizard-card > h2:first-child,
.gm-campaign-create .gs-wizard-card > header:first-child h2 {
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.gm-campaign-create .gs-wizard-card > p:first-of-type {
  margin: 0 0 18px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.55;
}

/* Step-body primitives shared by step1/step3/step4/step6 */
.gm-campaign-create .gm-step {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.gm-campaign-create .gm-fields {
  display: grid;
  gap: 18px;
}

.gm-campaign-create .gm-field-label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 7px;
  color: var(--gs-foreground);
  font-size: 13px;
  font-weight: 700;
}

.gm-campaign-create .gm-field-label button {
  display: inline-flex;
  align-items: center;
  padding: 0;
  color: var(--gs-muted-foreground);
  background: transparent;
  border: 0;
  cursor: help;
}

.gm-campaign-create .gm-field-label button svg {
  width: 14px;
  height: 14px;
}

.gm-campaign-create .gm-field-error {
  margin: 8px 0 0;
  color: var(--gs-danger);
  font-size: 12px;
}

.gm-campaign-create .gm-help {
  margin: 8px 0 0;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.5;
}

.gm-campaign-create .gm-inline-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}

.gm-campaign-create .gm-link {
  padding: 0;
  color: var(--gs-primary);
  font: inherit;
  font-weight: 700;
  background: transparent;
  border: 0;
  border-radius: var(--gs-radius-sm);
  cursor: pointer;
}

.gm-campaign-create .gm-modal-body {
  padding: 24px 28px 32px;
}

.gm-campaign-create .gm-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.gm-campaign-create .gm-modal-actions--padded {
  padding: 0 28px 28px;
}

/* Stat tiles */
.gm-campaign-create .gm-stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
}

.gm-campaign-create .gm-stat {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  padding: 12px 14px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gm-campaign-create .gm-stat__icon {
  width: 18px;
  height: 18px;
  color: var(--gs-muted-foreground);
}

.gm-campaign-create .gm-stat__label {
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
}

.gm-campaign-create .gm-stat__value {
  color: var(--gs-foreground);
  font-size: 18px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

/* Radio cards (§5.3): selection is blue, actions stay pink */
.gm-campaign-create .gm-choice-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.gm-campaign-create .gm-choice {
  min-width: 0;
}

/* gd-custom-radio (legacy kit): restyle its container and marks only */
.gm-campaign-create .gm-choice > div {
  height: 100%;
  margin: 0;
  padding: 16px 18px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  transition: border-color 150ms ease, background-color 150ms ease, box-shadow 150ms ease;
}

.gm-campaign-create .gm-choice > div:hover {
  border-color: var(--gs-border-strong);
}

.gm-campaign-create .gm-choice > div.border-primary {
  background: var(--gs-active-soft);
  border-color: var(--gs-active-border);
  box-shadow: var(--gs-active-shadow);
}

.gm-campaign-create .gm-choice > div > label {
  align-items: flex-start;
}

.gm-campaign-create .gm-choice > div > label > span {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  margin: 1px 0 0;
  background: var(--gs-surface);
  border: 1.5px solid var(--gs-border-strong);
  border-radius: 999px;
  box-shadow: none;
}

.gm-campaign-create .gm-choice > div.border-primary > label > span {
  background: var(--gs-active);
  border-color: var(--gs-active);
  box-shadow: inset 0 0 0 3px var(--gs-surface);
}

.gm-campaign-create .gm-choice > div > label > div {
  margin-left: 12px;
}

.gm-campaign-create .gm-choice > div > label > div > label {
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 650;
  line-height: 1.35;
}

.gm-campaign-create .gm-choice > div > label > div > div {
  margin-top: 4px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.5;
}

/* Hand-built radio card (step6 send options) */
.gm-campaign-create .gm-choice-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  height: 100%;
  padding: 16px 18px 16px 46px;
  text-align: left;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  cursor: pointer;
  transition: border-color 150ms ease, background-color 150ms ease, box-shadow 150ms ease;
}

.gm-campaign-create .gm-choice-card:hover {
  border-color: var(--gs-border-strong);
}

.gm-campaign-create .gm-choice-card.is-selected {
  background: var(--gs-active-soft);
  border-color: var(--gs-active-border);
  box-shadow: var(--gs-active-shadow);
}

.gm-campaign-create .gm-choice-card.is-disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.gm-campaign-create .gm-choice-card__radio {
  position: absolute;
  top: 18px;
  left: 18px;
  width: 18px;
  height: 18px;
  background: var(--gs-surface);
  border: 1.5px solid var(--gs-border-strong);
  border-radius: 999px;
}

.gm-campaign-create .gm-choice-card.is-selected .gm-choice-card__radio {
  background: var(--gs-active);
  border-color: var(--gs-active);
  box-shadow: inset 0 0 0 3px var(--gs-surface);
}

.gm-campaign-create .gm-choice-card__icon {
  width: 22px;
  height: 22px;
  color: var(--gs-muted-foreground);
}

.gm-campaign-create .gm-choice-card__title {
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 650;
  line-height: 1.35;
}

.gm-campaign-create .gm-choice-card__desc {
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.5;
}

/* Legacy kit inputs inside the wizard: align borders, radii and focus rings (containers only) */
.gm-campaign-create .gm-input.border,
.gm-campaign-create textarea.gm-input {
  color: var(--gs-foreground);
  border-color: var(--gs-border);
  border-radius: var(--gs-radius-sm);
  transition: border-color 120ms ease, box-shadow 120ms ease;
}

.gm-campaign-create .gm-input.border-danger,
.gm-campaign-create textarea.gm-input.border-danger {
  border-color: var(--gs-danger);
}

.gm-campaign-create .gm-input:focus-within,
.gm-campaign-create textarea.gm-input:focus {
  border-color: var(--gs-active-border);
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gm-campaign-create .gm-input > input {
  font-size: 14px;
  border-radius: var(--gs-radius-sm);
}

.gm-campaign-create .gm-input > .text-danger {
  font-size: 12px;
}

.gm-campaign-create .gm-dropdown > .rounded-lg {
  border-color: var(--gs-border);
  border-radius: var(--gs-radius-sm);
  font-size: 14px;
}

.gm-campaign-create .gm-dropdown > .rounded-lg.rounded-b-none {
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
}

.gm-campaign-create .gm-dropdown .shadow-lg {
  border-color: var(--gs-border);
  border-radius: 0 0 var(--gs-radius-sm) var(--gs-radius-sm);
  box-shadow: var(--gs-shadow-md);
}

.gm-campaign-create .vue-treeselect__control {
  min-height: 40px;
  border-color: var(--gs-border);
  border-radius: var(--gs-radius-sm);
  transition: border-color 120ms ease, box-shadow 120ms ease;
}

.gm-campaign-create .vue-treeselect--focused:not(.vue-treeselect--open) .vue-treeselect__control,
.gm-campaign-create .vue-treeselect--open .vue-treeselect__control {
  border-color: var(--gs-active-border);
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gm-campaign-create .error .vue-treeselect__control {
  border-color: var(--gs-danger);
}

.gm-campaign-create .vue-treeselect__placeholder,
.gm-campaign-create .vue-treeselect__single-value {
  font-size: 14px;
  line-height: 38px;
}

.gm-campaign-create .vue-treeselect__menu {
  border-color: var(--gs-border);
  border-radius: 0 0 var(--gs-radius-sm) var(--gs-radius-sm);
  box-shadow: var(--gs-shadow-md);
}

.gm-campaign-create .vue-treeselect__option--highlight {
  background: var(--gs-muted);
}

.gm-campaign-create .vue-treeselect__multi-value-item {
  color: var(--gs-active);
  background: var(--gs-active-soft);
  border-radius: var(--gs-radius-sm);
}

.gm-campaign-create .vue-treeselect__value-remove {
  color: var(--gs-active);
  border-left-color: var(--gs-active-border);
}

/* Quill (email basic editor) chrome inside the wizard */
.gm-campaign-create .ql-toolbar.ql-snow {
  border-color: var(--gs-border);
  border-radius: var(--gs-radius-sm) var(--gs-radius-sm) 0 0;
}

.gm-campaign-create .ql-container.ql-snow {
  border-color: var(--gs-border);
  border-radius: 0 0 var(--gs-radius-sm) var(--gs-radius-sm);
}

@media (max-width: 900px) {
  .gm-campaign-create .gm-wizard {
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 24px 16px 40px;
  }

  .gm-campaign-create .gm-wizard--with-banner {
    padding-top: 72px;
  }

  /* Sidebar collapses to a horizontal, scrollable step strip above the content */
  .gm-campaign-create .gm-wizard__sidebar {
    position: static;
    display: flex;
    gap: 6px;
    padding-bottom: 4px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .gm-campaign-create .gm-wizard__nav-heading {
    display: none;
  }

  .gm-campaign-create .gm-wizard__nav-item {
    flex: 0 0 auto;
    width: auto;
    min-height: 36px;
    border-left: 0;
    border-bottom: 3px solid transparent;
    border-radius: var(--gs-radius-sm);
  }

  .gm-campaign-create .gm-wizard__nav-item.is-active {
    border-left: 0;
    border-bottom-color: var(--gs-primary);
    border-radius: var(--gs-radius-sm) var(--gs-radius-sm) 0 0;
  }
}

@media (max-width: 640px) {
  .gm-campaign-create-shell__actions {
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .gm-campaign-create .gm-wizard__footer {
    grid-template-columns: 1fr;
  }

  .gm-campaign-create .gm-wizard__footer-end {
    justify-content: stretch;
  }

  .gm-campaign-create .gm-wizard__footer-end .gs-button {
    flex: 1 1 auto;
  }

  .gm-campaign-create .gm-modal-body {
    padding: 18px 18px 24px;
  }

  .gm-campaign-create .gm-modal-actions--padded {
    padding: 0 18px 18px;
  }
}
</style>
